import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  BdAdministrativeDistrictId,
  CareServiceArea,
} from '@bindoc-projects/geographic-domain-lib';
import { CareServiceAreaStore } from './care-service-area.store';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CareServiceAreaService {
  private readonly _httpClient = inject(HttpClient);
  private readonly store: CareServiceAreaStore;

  constructor() {
    const envService = inject(BdAppEnvironmentService);
    this.store = new CareServiceAreaStore(
      envService.load(ApiEndpointKey.BD_DISTRICT_URL),
    );
  }

  public search(query = ''): Observable<CareServiceArea[]> {
    const url = this.store.index();
    const params = new HttpParams().set('q', query);

    return this._httpClient.get<CareServiceArea[]>(url, { params });
  }

  public findAll(careServiceAreaIds: number[]): Observable<CareServiceArea[]> {
    const url = this.store.index();

    return this._httpClient.post<CareServiceArea[]>(url, {
      careServiceAreaIds: careServiceAreaIds,
    });
  }

  public findOne(careServiceAreaId: number): Observable<CareServiceArea> {
    const url = this.store.details(careServiceAreaId);

    return this._httpClient.get<CareServiceArea>(url);
  }

  public findItsAdministrativeRegions(
    careServiceAreaIds: number[],
  ): Observable<{ [careServiceAreaId: number]: number[] }> {
    const url = this.store.administrativeRegions();

    return this._httpClient.post<{ [careServiceAreaId: number]: number[] }>(
      url,
      { careServiceAreaIds: careServiceAreaIds },
    );
  }

  public findItsAdministrativeDistricts(
    careServiceAreaIds: number[],
  ): Observable<BdAdministrativeDistrictId[]> {
    const url = this.store.administrativeDistricts();

    return this._httpClient
      .post<number[]>(url, { careServiceAreaIds: careServiceAreaIds })
      .pipe(
        map((administrativeDistrictIds) =>
          administrativeDistrictIds.map((it) =>
            BdAdministrativeDistrictId.createFromNumber(it),
          ),
        ),
      );
  }

  public findItsPostalCodes(
    careServiceAreaIds: number[],
  ): Observable<string[]> {
    const url = this.store.postalCodes();

    return this._httpClient.post<string[]>(url, {
      careServiceAreaIds: careServiceAreaIds,
    });
  }
}
